import { first } from 'rxjs/internal/operators/first';

import { Deferred } from '../classes/deferred';
import { environment } from '../environment';

import { IAuthenticationService } from './interfaces/IAuthenticationService';
import { IUserSettingsService } from './interfaces/IUserSettingsService';

export class StorageService {
    private readonly storageProvider: Storage;
    private readonly userSettingsService: IUserSettingsService;
    private userEmail: string;

    private get canBackup() { return this.userSettingsService && this.userEmail; }

    public readonly storageType: 'session' | 'local';

    constructor(backupServices?: { userSettingsService: IUserSettingsService; authService: IAuthenticationService }, useSessionStorage?: boolean) {
        //handle storage providers
        if (useSessionStorage) {
            this.storageProvider = window.sessionStorage;
            this.storageType = 'session';
        } else {
            this.storageProvider = window.localStorage;
            this.storageType = 'local';
        }
        if (backupServices) {
            this.userSettingsService = backupServices.userSettingsService;
            backupServices.authService.GetCurrentUser().subscribe(user => {
                if (user) {
                    this.userEmail = user.emailAddress;
                } else {
                    this.userEmail = null;
                }
            });
        }
    }

    /**
     * Saves a provided value to storage
     * @param key - Key to store the value at
     * @param value - Value to store
     * @returns Boolean indicating success of the save
     */
    async set<T>(key: string, value: T, caller?: string, localOnly?: boolean) {
        try {
            this.storageProvider.setItem(key, JSON.stringify(value));
            if (!localOnly) {
                await this.setUserSetting<T>(key, value);
            }
            window.dispatchEvent(new CustomEvent('itemInserted', { detail: { key, caller } }));

        } catch (error) {
            return false;
        }
        return true;
    }

    /**
     * Retrieve the value stored at a specific key in the original base type it was saved as.
     * @remarks
     * Verify type coming back matches the type expected
     * @param key - Key to retrieve value from
     * @returns Parsed version of the string saved in Storage or null if parse fails or types do not match
     */
    async get<T>(key: string) {
        const res = this.storageProvider.getItem(key);
        if (!res) {
            const usRes = await this.getUserSetting<T>(key);
            if (usRes) { // Cache locally
                this.set(key, usRes, null, true);
                return usRes;
            }
        } else {
            return JSON.parse(res) as T;
        }
        return null;
    }

    /**
     * Set a property of a specified name to an object at or nested in another object at the key provided in Storage
     * @param key - Key of root object in Storage
     * @param nestedPath - Path to a nested object to modify ([] if no nesting present in use case)
     * @param propertyName - Property name to set on the object
     * @param value - Value of property to set on the object
     * @returns Returns boolean indicating success of the property setting
     */
    async setPropertyOfObject(key: string, nestedPath: string[], propertyName: string, value: any, caller?: string) {
        //get the object at the provided key and ensure it is real and an object
        let objAtKey = await this.get<any>(key);
        if (!objAtKey || typeof objAtKey !== 'object') {
            objAtKey = {};
        }
        //get a nested obj out of the object for modification via the path provided through the object hierarchy
        let currentPlace = objAtKey;
        for (const p of nestedPath) {
            currentPlace = currentPlace[p];
            if (!currentPlace) {
                return false;
            }
        }
        const objToModify = currentPlace;
        objToModify[propertyName] = value;
        return this.set(key, objAtKey, caller);
    }

    /**
     * Modifies an array at the key or nested in an object at the specified key at the specified nestedObjKey to have the value provided appended to it
     * @param key - Key that the object is stored in in Storage
     * @param nestedPath - Path to the array through the object hierarchy. Must end with property name of the array to modify or empty to modify an array at the provided storage key
     * @param value - Value to be appended to the array
     * @returns Boolean value indicating success of appending
     */
    async appendToArray(key: string, nestedPath: string[], value: any, caller?: string) {
        //get the object at the provided key and ensure it is real and an object
        const objAtKey = await this.get<any>(key);
        if (!objAtKey || typeof objAtKey !== 'object')  {
            return false;
        }
        //get the array out of the object for modification via the path provided through the object hierarchy
        let currentPlace = objAtKey;
        for (const p of nestedPath) {
            currentPlace = currentPlace[p];
            if (!currentPlace) {
                return false;
            }
        }
        const arr = currentPlace;
        if (!arr || !Array.isArray(arr)) {
            return false;
        }
        //add new value and save obj to Storage
        arr.push(value);
        return this.set(key, objAtKey, caller);
    }

    /**
     * Removes a value from an array at the key provided or nested in an object stored in Storage
     * @param key - Key the object is stored in in Storage
     * @param nestedPath - Path to the array through the object hierarchy. Must end with property name of the array to modify or empty if array is stored at the key in storage
     * @param value - Value to remove from the array
     * @returns Boolean indicating success of removal
     */
    async removeFromArray(key: string, nestedPath: string[], value: any, caller?: string) {
        //get the object at the provided key and ensure it is real and an object
        const objAtKey = await this.get<any>(key);
        if (!objAtKey || typeof objAtKey !== 'object') {
            return false;
        }
        //get the array out of the object for modification via the path provided through the object hierarchy
        let currentPlace = objAtKey;
        for (const p of nestedPath) {
            currentPlace = currentPlace[p];
            if (!currentPlace) {
                return false;
            }
        }
        let arr = currentPlace;
        if (!arr || !Array.isArray(arr)) {
            return false;
        }
        //ensure an element matching the specified value is actually present
        const elementIndex = arr.findIndex(item => item === value);
        if (elementIndex === -1) {
            return false;
        }
        //remove the found element and save it to storage
        arr = arr.splice(elementIndex, 1);
        return this.set(key, objAtKey, caller);
    }

    /**
     * Checks if the storage has the given key
     * @param key - Key the object is stored in in Storage
     * @returns Boolean indicating if the key exist
     */
    async hasKey(key: string) {
        let retVal = this.storageProvider.getItem(key) !== null;
        if (!retVal) {
            retVal = !!(await this.getUserSetting(key));
        }
        return retVal;
    }

    private async getUserSetting<T = any>(name: string) {
        if (!this.canBackup) {
            return null;
        }

        const retVal = new Deferred<T>();

        this.userSettingsService.GetSettingValue(name, environment.pdauthAppName, this.userEmail).pipe(first()).subscribe(result => {
            retVal.resolve(result);
        });

        return retVal.promise;
    }

    private async setUserSetting<T = any>(name: string, value: T) {
        if (this.canBackup) {
            await this.userSettingsService.SetSettingValue(name, value, this.userEmail, environment.pdauthAppName);
        }
    }
}
